import React from 'react';

import type {
  DynamicView,
  HeadingProps,
  ViewComponent,
  ViewRenderProps,
} from '../../types/dynamic_views';
import { ComponentRenderer } from './ComponentRenderer';

interface DynamicViewRendererProps extends ViewRenderProps {
  view: DynamicView;
  /** When true, skip the first heading component (used when the parent already renders the title) */
  skipFirstHeading?: boolean;
}

export const DynamicViewRenderer: React.FC<DynamicViewRendererProps> = ({
  view,
  onInteraction,
  interactive = true,
  className = '',
  skipFirstHeading = false,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic view data structure varies by view type
  const handleInteraction = (interactionType: string, data: any) => {
    if (onInteraction) {
      // Extract component_id from data for the parent handler
      const componentId = data.component_id || '';
      onInteraction(interactionType, componentId, data);
    }
  };

  const renderComponent = (
    component: ViewComponent,
    index: number
  ): React.ReactNode => {
    return (
      <ComponentRenderer
        key={component.id || `${component.type}-${index}`}
        component={component}
        onInteraction={interactive ? handleInteraction : undefined}
      />
    );
  };

  const { view_definition } = view;
  const { layout, components } = view_definition;

  // Apply layout configuration
  const layoutClasses = getLayoutClasses(layout);
  const combinedClasses = `dynamic-view ${layoutClasses} ${className}`.trim();

  return (
    <div className={combinedClasses} data-view-id={view.id}>
      {/* Dynamic Components */}
      <div className='dynamic-view-content space-y-3'>
        {components.map((component, index) => {
          // Skip the first heading only when its text matches the view title
          // (avoids removing a heading that is different from the sidebar header)
          if (
            skipFirstHeading &&
            index === 0 &&
            component.type === 'heading' &&
            (component.props as HeadingProps).text === view.title
          ) {
            return null;
          }
          return renderComponent(component, index);
        })}
      </div>

      {/* View Footer - using semantic color tokens */}
      <div className='dynamic-view-footer mt-6 pt-3 border-t border-border-secondary'>
        <div className='text-[12px] text-fg-quaternary'>
          Created {new Date(view.created_at).toLocaleString()}
          {view.last_viewed_at && (
            <span>
              {' '}
              • Last viewed {new Date(view.last_viewed_at).toLocaleString()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic view data structure varies by view type
function getLayoutClasses(layout: any): string {
  if (!layout) {
    return '';
  }

  const { type, props = {} } = layout;
  let classes = '';

  if (type === 'container') {
    // Container layout classes
    const maxWidth = props.maxWidth || 'lg';
    const padding = props.padding || 'md';

    const maxWidthClasses = {
      sm: 'max-w-screen-sm',
      md: 'max-w-screen-md',
      lg: 'max-w-screen-lg',
      xl: 'max-w-screen-xl',
      '2xl': 'max-w-screen-2xl',
      full: 'max-w-none',
    };

    const paddingClasses = {
      none: 'p-0',
      sm: 'p-2',
      md: 'p-4',
      lg: 'p-6',
      xl: 'p-8',
    };

    classes = `mx-auto ${maxWidthClasses[maxWidth as keyof typeof maxWidthClasses] || maxWidthClasses.lg} ${
      paddingClasses[padding as keyof typeof paddingClasses] ||
      paddingClasses.md
    }`;
  }

  return classes;
}

export default DynamicViewRenderer;
